import { createReceipt, updateReceipt } from "@/modules/finances/receipts/services/receipts.services"
import { Receipt, TypeReceipts } from "@/modules/finances/receipts/types"
import { ReceiptCategory } from "@/modules/settings/receipt-categories"
import { fetchReceiptCategories } from "@/modules/settings/receipt-categories/services/receipt-categories.serivces"
import { StatusEnum } from "@/utils/constants"
import { formatterInputNumber } from "@/utils/helpers"
import { DatePicker, Form, Input, InputNumber, Modal, Select, message } from "antd"
import TextArea from "antd/es/input/TextArea"
import dayjs from "dayjs"
import React, { useEffect, useState } from "react"

interface ReceiptFormProps {
  visible: boolean
  mode: 'create' | 'update'
  initialData?: Receipt
  cashBookId: string
  onCancel: () => void
  onSubmit: () => void
  loading?: boolean
}

const ReceiptForm: React.FC<ReceiptFormProps> = ({
  visible,
  mode,
  initialData,
  cashBookId,
  onCancel,
  onSubmit,
  loading = false
}) => {
  const [form] = Form.useForm()
  const [receiptCategories, setReceiptCategories] = useState<ReceiptCategory[]>([])
  const [typeReceipt, setTypeReceipt] = useState<TypeReceipts>(TypeReceipts.EXPENSE)
  const [buttonLoading, setButtonLoading] = useState<boolean>(false)

  const getReceiptCategories = async () => {
    try {
      const response = await fetchReceiptCategories({ type: typeReceipt })
      if (response?.data) {
        setReceiptCategories(response.data.items)
      }
    } catch (error) {
      console.error("Error fetching receipt categories:", error)
    }
  }

  useEffect(() => {
    if (visible) {
      getReceiptCategories()
    }
  }, [visible, typeReceipt])

  useEffect(() => {
    if (visible && mode === 'update' && initialData) {
      setTypeReceipt(initialData.type as TypeReceipts)
      form.setFieldsValue({
        name: initialData.name,
        amount: initialData.amount,
        category_id: initialData.category_id,
        type: initialData.type,
        notes: initialData.notes,
        date_happened: dayjs(initialData.date_happened),
      })
    } else if (visible && mode === 'create') {
      form.resetFields()
      form.setFieldsValue({
        type: TypeReceipts.EXPENSE,
        date_happened: dayjs(),
      })
    }
  }, [visible, mode, initialData, form])

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setButtonLoading(true)

      const receiptData: Receipt = {
        ...values,
        date_happened: values.date_happened.toDate(),
        status: StatusEnum.ACTIVE,
      }

      if (mode === 'create') {
        const response = await createReceipt(receiptData)
        if (response?.statusCode === 200) {
          message.success('Thêm thu chi thành công!')
          form.resetFields()
          onSubmit()
        }
      } else if (mode === 'update' && initialData?.id) {
        const response = await updateReceipt(initialData.id, receiptData)
        if (response?.statusCode === 200) {
          message.success('Cập nhật thu chi thành công!')
          onSubmit()
        }
      }
    } catch (error: any) {
      console.error("Receipt operation failed:", error)
      message.error(error?.message || 'Có lỗi xảy ra!')
    } finally {
      setButtonLoading(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    onCancel()
  }

  return (
    <Modal
      title={mode === 'create' ? "Thêm thu chi mới" : "Cập nhật thu chi"}
      open={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={buttonLoading}
      okText={mode === 'create' ? "Thêm" : "Cập nhật"}
      cancelText="Hủy"
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Form.Item
          label="Loại thu chi"
          name="type"
          rules={[{ required: true, message: 'Vui lòng chọn loại thu chi!' }]}
        >
          <Select
            placeholder="Chọn loại thu chi"
            onChange={(value) => setTypeReceipt(value)}
            options={[
              { value: TypeReceipts.INCOME, label: 'Thu' },
              { value: TypeReceipts.EXPENSE, label: 'Chi' },
            ]}
          />
        </Form.Item>

        <Form.Item
          label="Tên giao dịch"
          name="name"
          rules={[{ required: true, message: 'Vui lòng nhập tên giao dịch!' }]}
        >
          <Input placeholder="Nhập tên giao dịch" />
        </Form.Item>

        <Form.Item
          label="Danh mục"
          name="category_id"
          rules={[{ required: true, message: 'Vui lòng chọn danh mục!' }]}
        >
          <Select
            placeholder="Chọn danh mục"
            options={receiptCategories.map(cat => ({
              value: cat.id,
              label: cat.category
            }))}
          />
        </Form.Item>

        <Form.Item
          label="Số tiền"
          name="amount"
          rules={[{ required: true, message: 'Vui lòng nhập số tiền!' }]}
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder="Nhập số tiền"
            min={0}
            formatter={value => formatterInputNumber(value)}
            addonAfter="VNĐ"
            step={10000}
          />
        </Form.Item>

        <Form.Item
          label="Ngày xảy ra"
          name="date_happened"
          rules={[{ required: true, message: 'Vui lòng chọn ngày xảy ra!' }]}
        >
          <DatePicker
            style={{ width: '100%' }}
            format="DD/MM/YYYY HH:mm"
            showTime={{ format: 'HH:mm' }}
            placeholder="Chọn ngày và giờ"
          />
        </Form.Item>

        <Form.Item
          label="Ghi chú"
          name="notes"
        >
          <TextArea
            rows={4}
            placeholder="Nhập ghi chú (không bắt buộc)"
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default ReceiptForm 